/* eslint-disable radix */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { IconButton, Typography, styled } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Stack } from '@mui/system';
import { ColorsV2 } from 'app/constants/color';
import { parseAndFormatDate } from 'app/utils';

export default function IdentityList({ data, flag }) {
  const [structuredData, setStructuredData] = React.useState(
    data?.length > 0 ? (flag === 'report' ? data : data?.slice(0, 3)) : []
  );
  const CustomizedTableCell = styled(TableCell)({
    width: '10%',
    fontWeight: '700',
    fontSize: '0.7em',
    lineHeight: '20px',
    color: flag !== 'report' && ColorsV2.white,
    fontFamily: 'Roboto, sans-serif',
  });

  const CustomizedTableCell2 = styled(TableCell)({
    fontSize: '0.6em',
    color: flag !== 'report' && ColorsV2.white,
    fontFamily: 'Roboto, sans-serif',
  });

  const { t } = useTranslation('translation');
  const extractedProducts = [];
  const generateKey = (productName, productId) => `${productName}-${productId}`;
  for (const categoryObj of data || []) {
    const products = categoryObj.dataClassLevels;

    for (const productObj of products) {
      const productName = productObj.name;
      const productId = productObj.id;
      const key = generateKey(productName, productId);

      if (!extractedProducts.some(obj => generateKey(obj.productName, obj.productId) === key)) {
        extractedProducts.push({ productName, productId });
      }
    }
  }

  const newArr = extractedProducts?.sort((a, b) => {
    const idA = parseInt(a.productId?.slice(1));
    const idB = parseInt(b.productId?.slice(1));
    return idA - idB;
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        background: ColorsV2.primary,
        border: flag !== 'report' && `1px solid ${ColorsV2.outline}`,
      }}
    >
      <Table
        sx={{
          borderCollapse: 'separate',
          minWidth: 650,
          '& th': {
            color: ColorsV2.white,
            backgroundColor: ColorsV2.secondary,
          },
          '& tr': {
            color: flag !== 'report' && ColorsV2.white,
          },
        }}
        aria-label='simple table'
      >
        <TableHead>
          <TableRow>
            <CustomizedTableCell> {t(`identify_headings.${'DataSource'}`)}</CustomizedTableCell>
            <CustomizedTableCell style={{ width: '9%' }} align='left'>
              {t(`identify_headings.Status`)}
            </CustomizedTableCell>
            <CustomizedTableCell style={{ width: '20%' }} align='center'>
              {t(`identify_headings.LastScan`)}
            </CustomizedTableCell>
            {newArr?.map(item => (
              <CustomizedTableCell key={item.productId} style={{ width: '22%' }} align='center'>
                {item.productName}
              </CustomizedTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {structuredData?.map(row => (
            <TableRow
              key={row.id}
              sx={{
                border: 0,
                borderColor: 'transparent',
                height: 'auto',
                '&:nth-of-type(odd)': {
                  backgroundColor: flag !== 'report' ? 'transparent' : 'white',
                },
                '&:nth-of-type(even)': {
                  backgroundColor: flag !== 'report' ? 'rgba(217, 217, 217, 0.15)' : 'white',
                },
              }}
            >
              <CustomizedTableCell scope='row'>{row.name}</CustomizedTableCell>
              <CustomizedTableCell2 align='left'>
                {flag === 'report' ? (
                  <div>
                    {row.scanStatus === 'complete'
                      ? t('login_headings.helper_text.finished')
                      : t(`login_headings.helper_text.${row.scanStatus}`)}
                  </div>
                ) : row.scanStatus === 'complete' ? (
                  t('login_headings.helper_text.finished')
                ) : (
                  t(`login_headings.helper_text.${row.scanStatus}`)
                )}
              </CustomizedTableCell2>

              <CustomizedTableCell2 align='center' sx={{ padding: '0' }}>
                {row?.lastScan ? parseAndFormatDate(row?.lastScan) : 'N/A'}
              </CustomizedTableCell2>

              {newArr.map(({ productId }) => (
                <CustomizedTableCell2 key={productId} align='center' sx={{ padding: '0' }}>
                  {row?.dataClassLevels?.find(item => item.id === productId)?.nrOfObjects || 'N/A'}
                </CustomizedTableCell2>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {structuredData.length === 3 && data.length > 3 && flag !== 'report' && (
        <Stack
          sx={{
            width: '100%',
            padding: '20px 0px',
          }}
          direction='row'
          justifyContent='center'
          alignItems='center'
          spacing={2}
        >
          <Typography
            sx={{
              fontSize: flag === 'report' ? '0.5em' : '0.7em',
              fontWeight: '700',
              lineHeight: '20px',
              fontFamily: 'Lato, sans-serif',
              color: ColorsV2.white,
              cursor: 'pointer',
            }}
            onClick={() => {
              setStructuredData(data);
            }}
          >
            {t(`identify_headings.${'ViewAllSources'}`)}
          </Typography>
          <IconButton
            onClick={() => {
              setStructuredData(data);
            }}
          >
            <KeyboardArrowDownIcon sx={{ color: ColorsV2.white }} />
          </IconButton>
        </Stack>
      )}
      {structuredData.length > 3 && flag !== 'report' && (
        <Stack
          sx={{
            width: '100%',
            padding: '20px 0px',
          }}
          direction='row'
          justifyContent='center'
          alignItems='center'
          spacing={2}
        >
          <Typography
            sx={{
              fontSize: flag === 'report' ? '0.5em' : '0.7em',
              fontWeight: '700',
              lineHeight: '20px',
              fontFamily: 'Lato, sans-serif',
              color: ColorsV2.white,
              cursor: 'pointer',
            }}
            onClick={() => {
              setStructuredData(data.slice(0, 3));
            }}
          >
            {t(`identify_headings.${'ViewMinimizedSources'}`)}
          </Typography>
          <IconButton
            onClick={() => {
              setStructuredData(data.slice(0, 3));
            }}
          >
            <KeyboardArrowUpIcon sx={{ color: ColorsV2.white }} />
          </IconButton>
        </Stack>
      )}
    </TableContainer>
  );
}
